import React, {useEffect} from 'react';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import {BaseModal} from '@/components/modals';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {buildURL} from '@/utils/urlBuilder';
import CellLoader from '@/components/loading/CellLoader';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';

interface UserLogin {
    createdOn: string;
    ip: string;
    browserAgent: string;
}

interface UserLoginsResponse {
    userLogins: UserLogin[];
    pagesList: number[];
}

interface UserLoginsModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    activeUserId: number;
}

const userLoginsUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<{data: UserLoginsResponse}> =>
    axios.get(url, {params: paramsDict})
        .catch((error: any) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const userLoginsUpdateRequestDebounced = AwesomeDebouncePromise(userLoginsUpdateRequest, 350);

const UserLoginsModal: React.FC<UserLoginsModalProps> = React.memo((
    {
        modalOpen,
        closeModal,
        activeUserId,
    },
) => {
    const userLogins = useStoreState((state: any) => state.usersLogins.items);
    const storeUserLogins = useStoreActions((actions: any) => actions.usersLogins.loadUserLogins);
    const {formatDateTime} = useFormattedDateTime();

    const searchPagesList = useStoreState((state: any) => state.usersLogins.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions: any) => actions.usersLogins.setSearchPagesList);

    const searchCurrentPage = useStoreState((state: any) => state.usersLogins.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions: any) => actions.usersLogins.setSearchCurrentPage);

    const loadingUserLogins = useStoreState((state: any) => state.usersLogins.loading);
    const setLoadingUserLogins = useStoreActions((actions: any) => actions.usersLogins.setLoading);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setLoadingUserLogins(true);

            const paramsDict = {
                page: searchCurrentPage,
            };

            const response = await userLoginsUpdateRequestDebounced(
                buildURL('usersLogins', {obj_id: activeUserId}), paramsDict,
            );

            storeUserLogins(response.data.userLogins);
            storeSearchPagesList(response.data.pagesList);

            setLoadingUserLogins(false);
        };

        if (activeUserId !== 0) {
            fetchData();
        }
    }, [
        searchCurrentPage,
        storeSearchPagesList,
        storeUserLogins,
        activeUserId,
    ]);

    const pageChanged = (e: React.MouseEvent<HTMLElement>): void => {
        if (e.target && (e.target as HTMLElement).textContent) {
            storeSearchCurrentPage(parseInt((e.target as HTMLElement).textContent!, 10));
        }
    };

    const handlePageChange = (page: number) => {
        storeSearchCurrentPage(page);
    };

    const columns = [
        {
            id: 'i18ntthdatecreated4text',
            header: i18n.t('th_date_created.text'),
            accessorFn: (cell: UserLogin): string => {
                if (!cell.createdOn || cell.createdOn === '-') {
                    return '-';
                }

                return formatDateTime(
                    cell.createdOn,
                    true,
                    {setZone: false},
                );
            },
        },
        {
            header: i18n.t('th_text_ip.text'),
            accessorKey: 'ip',
        },
        {
            header: i18n.t('th_browser_agent.text'),
            accessorKey: 'browserAgent',
        },
    ];

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            title={i18n.t('jt_user_logins.text')}
            size="xl"
        >
            {
                loadingUserLogins ? (
                    <CellLoader />
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                <TableComponent data={userLogins} columns={columns} />
                                <PaginationComponent
                                    pages={searchPagesList}
                                    currentPage={searchCurrentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </>
                )
            }
        </BaseModal>
    );
});

export default UserLoginsModal; 